import { supabaseClient } from '../supabase.ts';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { applyFilters } from '../filter.js';
import { commonQueryProps } from './cache.js';
import type { OrderByOption, LimitOffsetOption } from '../../types/supabase.ts';

// Type for audit record based on _{table}_audit structure
export interface AuditRecord {
  id: number;
  parent_id: number;
  created_by: number;
  transaction_time: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: number;
}

// Interface for buildQueryForRecordAudit parameters
interface BuildRecordAuditQueryParams {
  tableName: string;
  schemaName: string;
  conditions?: any | null;
  orderBy?: OrderByOption | null;
  limitOffset?: LimitOffsetOption | null;
  withCount?: boolean;
}

/**
 * Builds the Supabase query for fetching audit records for a specific table
 * @param params - Query parameters including tableName, conditions, ordering, pagination, and count
 * @returns PostgrestBuilder query for _{table}_audit with all necessary columns
 */
export const buildQueryForRecordAudit = ({ 
  tableName,
  schemaName,
  conditions, 
  orderBy, 
  limitOffset, 
  withCount 
}: BuildRecordAuditQueryParams) => {
  const auditTableName = `_${tableName}_audit`;

  let query = supabaseClient
    .schema(schemaName as any)
    .from(auditTableName as any)
    .select(`
      id,
      parent_id,
      created_by,
      transaction_time,
      operation,
      old_data,
      new_data
    `, { count: withCount ? 'exact' : undefined });

  // Apply filters if any
  if (conditions) {
    query = applyFilters(query, conditions);
  }

  // Apply ordering if specified
  if (orderBy) {
    const ascending = orderBy.direction === 'ascending';
    query = query.order(orderBy.column, { ascending });
  } else {
    // Default ordering
    query = query.order('transaction_time', { ascending: false });
  }

  // Apply pagination if specified
  if (limitOffset) {
    const { limit = 100, offset = 0 } = limitOffset;
    query = query.range(offset, offset + limit - 1); // Range is inclusive
  }

  return query;
};

// Hook options interface
interface UseRecordAuditOptions {
  tableName: string;
  schemaName: string;
  enabled?: boolean;
  conditions?: any | null;
  orderBy?: OrderByOption | null;
  limitOffset?: LimitOffsetOption | null;
  withCount?: boolean;
}

// Hook result interface
interface UseRecordAuditResult {
  data: AuditRecord[] | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch audit records data using React Query
 * @param options - Options including tableName, enabled flag, conditions, ordering, and pagination
 * @returns Audit records data with loading and error states
 */
export const useRecordAudit = ({ 
  tableName,
  schemaName,
  enabled, 
  conditions, 
  orderBy, 
  limitOffset, 
  withCount 
}: UseRecordAuditOptions): UseRecordAuditResult => {
  // Only build query if tableName is provided
  const query = tableName && schemaName ? buildQueryForRecordAudit({ tableName, schemaName, conditions, orderBy, limitOffset, withCount }) : null;

  const { data, error, isPending } = useQuery(
    query as any,
    {
      ...commonQueryProps,
      enabled: enabled && !!tableName && !!schemaName
    }
  );

  if (isPending || error) {
    return {
      data: null,
      error,
      isLoading: isPending
    };
  }

  return {
    data: (data as any) as AuditRecord[] || [],
    error: null,
    isLoading: false
  };
};
